// frontend/src/components/admin/outreach/OutreachQueue.js
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { outreachService } from '../../../services/outreachService';

export function OutreachQueue() {
  const [items, setItems] = useState([]);
  const [tierFilter, setTierFilter] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editingSubject, setEditingSubject] = useState('');
  const [editingBody, setEditingBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [inboxHealth, setInboxHealth] = useState(null);
  const [inboxHealthError, setInboxHealthError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await outreachService.listPendingDrafts({ tier: tierFilter || undefined });
      setItems(data.items);
      setSelectedIdx(0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tierFilter]);

  const loadInboxHealth = useCallback(async () => {
    setInboxHealthError(null);
    try {
      const data = await outreachService.getInboxHealth();
      setInboxHealth(data);
    } catch (e) {
      setInboxHealthError(e?.response?.data?.detail || e.message || 'Inbox health unavailable');
      setInboxHealth(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadInboxHealth(); }, [loadInboxHealth]);

  const selected = items[selectedIdx];

  // Sync edit fields when selection changes
  useEffect(() => {
    if (selected) {
      setEditingSubject(selected.subject);
      setEditingBody(selected.body);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.draft_id]);

  const moveSelection = useCallback((delta) => {
    setSelectedIdx((i) => Math.max(0, Math.min(items.length - 1, i + delta)));
  }, [items.length]);

  const approveDisabled = busy || (inboxHealth ? !inboxHealth.all_mature : false);

  const onApprove = useCallback(async () => {
    if (!selected || busy) return;
    if (inboxHealth && !inboxHealth.all_mature) {
      // Server will reject with 409 anyway; short-circuit so the operator
      // gets immediate feedback instead of a round-trip.
      setError('Sender inboxes are not yet warmed — see banner at top of queue.');
      return;
    }
    setBusy(true);
    try {
      // If subject/body were edited, persist before approving
      if (editingSubject !== selected.subject || editingBody !== selected.body) {
        await outreachService.editDraft(selected.draft_id, {
          subject: editingSubject, body: editingBody,
        });
      }
      await outreachService.approveDraft(selected.draft_id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Approve failed');
      // If the failure was a 409 from the maturity guard, re-fetch health so
      // the banner reflects current state (operator may have been viewing
      // stale data).
      if (e?.response?.status === 409) {
        loadInboxHealth();
      }
    } finally {
      setBusy(false);
    }
  }, [selected, editingSubject, editingBody, busy, load, inboxHealth, loadInboxHealth]);

  const onReject = useCallback(async () => {
    if (!selected || busy) return;
    const reason = window.prompt('Reject reason (optional):') ?? '';
    setBusy(true);
    try {
      await outreachService.rejectDraft(selected.draft_id, { reason });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  }, [selected, busy, load]);

  // Keyboard shortcuts: j/k to navigate, a to approve, r to reject, e focus edit
  useEffect(() => {
    const onKey = (ev) => {
      // Skip if user is typing in textarea/input
      const tag = ev.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (ev.key === 'j') { ev.preventDefault(); moveSelection(1); }
      else if (ev.key === 'k') { ev.preventDefault(); moveSelection(-1); }
      else if (ev.key === 'a') { ev.preventDefault(); onApprove(); }
      else if (ev.key === 'r') { ev.preventDefault(); onReject(); }
      else if (ev.key === 'e') {
        ev.preventDefault();
        document.getElementById('outreach-edit-subject')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveSelection, onApprove, onReject]);

  return (
    <div className="p-6 flex flex-col h-screen">
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-2xl font-semibold">Outreach — Review Queue</h1>
        <div className="text-xs text-ink-muted">
          shortcuts: <kbd>j</kbd>/<kbd>k</kbd> navigate • <kbd>a</kbd> approve • <kbd>r</kbd> reject • <kbd>e</kbd> edit
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="tier-filter" className="text-sm text-ink-muted mr-2">Tier:</label>
        <select id="tier-filter" value={tierFilter} onChange={e => setTierFilter(e.target.value)}
          className="border rounded px-2 py-1 text-sm border-border-strong">
          <option value="">All</option>
          <option value="deep">Deep</option>
          <option value="light">Light</option>
        </select>
        <span className="ml-4 text-sm text-ink-muted">{items.length} pending</span>
      </div>

      {inboxHealthError && (
        <div className="mb-3 p-2 border border-warning-border rounded-control bg-warning-tint text-sm text-warning">
          Inbox health check unavailable: {inboxHealthError}. Approve is allowed but server-side
          guard may still reject sends.
        </div>
      )}
      {inboxHealth && inboxHealth.all_mature && (
        <div className="mb-3 p-2 border border-success-border rounded-control bg-success-tint text-sm text-success flex items-center gap-2">
          <span className="font-medium">✓ All {inboxHealth.inboxes.length} sender inboxes mature</span>
          <span className="text-success text-xs">
            ({inboxHealth.inboxes.map(i => `${i.email.split('@')[0]} ${i.sent_7d_avg}/d`).join(', ')})
          </span>
        </div>
      )}
      {inboxHealth && !inboxHealth.all_mature && (
        <div className="mb-3 p-3 border border-warning-border rounded-control bg-warning-tint text-sm text-warning">
          <p className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />Sender inboxes still warming — Approve is disabled.</p>
          {inboxHealth.error && (
            <p className="text-xs text-warning mb-1">{inboxHealth.error}</p>
          )}
          <ul className="text-xs space-y-0.5">
            {inboxHealth.inboxes.map(i => (
              <li key={i.email} className={i.mature ? 'text-success' : ''}>
                {i.mature ? '✓' : '✗'} <span className="font-mono">{i.email}</span> —{' '}
                {i.mature ? `${i.sent_7d_avg}/day avg, ${i.placement_pct ?? 0}% placement` : i.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && <div>Loading…</div>}
      {error && <div className="text-danger mb-3">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="text-ink-muted">Queue is empty. Nice.</div>
      )}

      {!loading && items.length > 0 && (
        <div className="flex gap-4 flex-1 overflow-hidden">
          {/* Left: list */}
          <div className="w-1/3 overflow-y-auto border rounded">
            {items.map((it, i) => (
              <button
                key={it.draft_id}
                onClick={() => setSelectedIdx(i)}
                className={`w-full text-left p-2 border-b text-sm ${i === selectedIdx ? 'bg-accent-tint' : 'bg-white hover:bg-surface-sunken'}`}
              >
                <div className="font-medium">{it.shop_name}</div>
                <div className="text-xs text-ink-muted">
                  {it.city}, {it.state} • ⭐ {it.google_rating} ({it.google_review_count})
                </div>
                <div className="text-xs text-ink-soft mt-1 truncate">{it.subject}</div>
                <div className="text-xs text-ink-faint">tier: {it.tier} • score: {it.quality_score?.toFixed?.(2)}</div>
              </button>
            ))}
          </div>

          {/* Right: details + edit */}
          <div className="flex-1 overflow-y-auto border rounded p-4">
            {selected && (
              <>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h2 className="text-lg font-semibold">{selected.shop_name}</h2>
                    <div className="text-sm text-ink-muted">
                      {selected.contact_email}
                      {selected.website_url && (
                        <> • <a href={selected.website_url} target="_blank" rel="noopener noreferrer" className="text-accent underline">website</a></>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={onReject} disabled={busy}
                      className="px-3 py-1 text-sm bg-surface-sunken hover:bg-surface-sunken/80 rounded disabled:opacity-50">
                      Reject (r)
                    </button>
                    <button
                      onClick={onApprove}
                      disabled={approveDisabled}
                      title={inboxHealth && !inboxHealth.all_mature ? 'Sender inboxes still warming' : undefined}
                      className="px-3 py-1 text-sm bg-accent text-white hover:bg-accent-hover rounded disabled:opacity-50 disabled:cursor-not-allowed">
                      {busy ? 'Sending…' : 'Approve & Send (a)'}
                    </button>
                  </div>
                </div>

                <div className="mb-3 text-xs text-ink-muted flex gap-4">
                  <span>tier: <strong>{selected.tier}</strong></span>
                  <span>quality: <strong>{selected.quality_score?.toFixed?.(2)}</strong></span>
                  <span>prompt: {selected.prompt_version}</span>
                  <span>model: {selected.model_used}</span>
                </div>

                {selected.homepage_snippet && (
                  <details className="mb-3 text-xs text-ink-muted">
                    <summary className="cursor-pointer">Homepage snippet</summary>
                    <p className="mt-1 italic">{selected.homepage_snippet}</p>
                  </details>
                )}

                {Array.isArray(selected.recent_reviews) && selected.recent_reviews.length > 0 && (
                  <details className="mb-3 text-xs text-ink-muted">
                    <summary className="cursor-pointer">Recent reviews ({selected.recent_reviews.length})</summary>
                    <ul className="mt-1 space-y-1">
                      {selected.recent_reviews.slice(0, 3).map((r, i) => (
                        <li key={i} className="border-l-2 border-border-default pl-2">{r.text || JSON.stringify(r)}</li>
                      ))}
                    </ul>
                  </details>
                )}

                <label className="block text-sm font-medium mt-2">Subject</label>
                <input
                  id="outreach-edit-subject"
                  type="text"
                  value={editingSubject}
                  onChange={e => setEditingSubject(e.target.value)}
                  className="w-full border rounded px-2 py-2 text-sm font-mono mb-2 border-border-strong"
                />

                <label className="block text-sm font-medium">Body</label>
                <textarea
                  value={editingBody}
                  onChange={e => setEditingBody(e.target.value)}
                  rows={12}
                  className="w-full border rounded px-2 py-2 text-sm font-mono border-border-strong"
                />
                <p className="text-xs text-ink-faint mt-1">
                  CAN-SPAM footer (commercial line + address + unsubscribe link) is appended at send time — don't add it here.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
